import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { AccessionNumberFormat } from "generated/graphql";
import { type ReactNode, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AccessionNumberInput } from "src/components/accession-number-input";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { match } from "ts-pattern";
import type { z } from "zod";
import { validateAccessionInput } from "../schema";
import { accessionNumberSchema } from "./schema";

const refinedAccessionNumberSchema = accessionNumberSchema.superRefine(
  validateAccessionInput,
);

export const AccessionNumberDialog = ({
  open,
  onOpenChange,
  children,
  onSubmit,
  initialValues,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    value: Omit<z.infer<typeof refinedAccessionNumberSchema>, "accessionDate">,
  ) => void;
  children: ReactNode;
  initialValues: Partial<z.infer<typeof refinedAccessionNumberSchema>>;
}) => {
  const defaultValues = useCallback(
    () => ({
      accessionDate: initialValues.accessionDate ?? new Date(),
      accessionNumberFormat: initialValues.accessionNumberFormat,
      accessionNumberSuffix: initialValues.accessionNumberSuffix ?? undefined,
      exogenousAccessionNumber:
        initialValues.exogenousAccessionNumber ?? undefined,
    }),
    [initialValues],
  );
  const form = useForm<z.infer<typeof refinedAccessionNumberSchema>>({
    defaultValues: defaultValues(),
    resolver: zodResolver(refinedAccessionNumberSchema),
  });

  const tab = form.watch("accessionNumberFormat") ?? "AUTOMATIC";

  useEffect(() => {
    if (!open) {
      return;
    }
    form.reset(defaultValues());
  }, [open, form, defaultValues]);

  const handleSubmit = (data: z.infer<typeof refinedAccessionNumberSchema>) => {
    match(data.accessionNumberFormat)
      .with(AccessionNumberFormat.PrefixSuffix, () => {
        onSubmit({
          accessionNumberSuffix: data.accessionNumberSuffix,
          accessionNumberFormat: data.accessionNumberFormat,
        });
      })
      .with(AccessionNumberFormat.Exogenous, () => {
        onSubmit({
          accessionNumberFormat: data.accessionNumberFormat,
          exogenousAccessionNumber: data.exogenousAccessionNumber,
        });
      })
      .otherwise(() => {
        onSubmit({
          accessionNumberFormat: undefined,
          exogenousAccessionNumber: undefined,
          accessionNumberSuffix: undefined,
        });
      });

    onOpenChange(false);
  };

  const editing =
    initialValues.accessionNumberSuffix != null ||
    initialValues.exogenousAccessionNumber != null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
        {children}
      </DialogTrigger>
      <FormProvider {...form}>
        <DialogContent className="gap-5!" asChild>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit(handleSubmit)(e);
            }}
          >
            <DialogHeader className="space-y-4">
              <DialogTitle className="flex items-center gap-2">
                {editing ? "Change" : "Set"} accession number
              </DialogTitle>
            </DialogHeader>
            <AccessionNumberInput tab={tab} showAutomaticTab={true} />
            <DialogFooter>
              <DialogClose asChild>
                <Button>Cancel</Button>
              </DialogClose>
              <Button
                variant="primary"
                type="submit"
                testId="save-accession-number-button"
              >
                {editing ? "Change" : "Set"} accession number
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
